// API Gateway Configuration for BigSystemOne
// This replaces direct Lambda URLs with API Gateway endpoints

export const API_GATEWAY_CONFIG = {
  // API Gateway Base URL (update after deployment)
  baseUrl: process.env.REACT_APP_API_GATEWAY_URL || 'https://diuh85h4d5.execute-api.us-east-1.amazonaws.com/prod',
  
  // Stage
  stage: process.env.REACT_APP_API_STAGE || 'prod',
  
  // Authentication
  auth: {
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'us-east-1_Jw9kHxDre',
    clientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '2sjuuubp9b9332tc6rf4p3t1fe',
    region: process.env.REACT_APP_AWS_REGION || 'us-east-1'
  },
  
  // Global auth token getter (set by app initialization)
  tokenGetter: null as (() => Promise<string | null>) | null,
  
  // Set the token getter function (called during app initialization)
  setTokenGetter: (getter: () => Promise<string | null>) => {
    API_GATEWAY_CONFIG.tokenGetter = getter;
  },
  
  // CORS is handled by API Gateway - no need to configure here
  
  // Request interceptor to add authentication headers
  requestInterceptor: async (config: any) => {
    let token: string | null = null;
    if (API_GATEWAY_CONFIG.tokenGetter) {
      try {
        token = await API_GATEWAY_CONFIG.tokenGetter();
      } catch {
        // Fall through to storage-based token below.
      }
    }
    if (!token) {
      token = localStorage.getItem('idToken') || sessionStorage.getItem('idToken');
    }
    if (token) {
      config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
    }
    return config;
  },
  
  // Response interceptor for error handling
  responseInterceptor: {
    onSuccess: (response: any) => response,
    onError: (error: any) => {
      if (error.response?.status === 401) {
        // Token expired or invalid - redirect to login
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  }
};

// API Endpoints mapping to API Gateway paths
export const API_GATEWAY_ENDPOINTS = {
  // Core Data API
  enterprises: () => `${API_GATEWAY_CONFIG.baseUrl}/api/enterprises`,
  enterprise: (id: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/enterprises/${id}`,
  concepts: (enterpriseId: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/enterprises/${enterpriseId}/concepts`,
  franchisees: (enterpriseId: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/enterprises/${enterpriseId}/franchisees`,
  
  // Menu Management API
  menuItems: (enterpriseId?: string) => 
    enterpriseId 
      ? `${API_GATEWAY_CONFIG.baseUrl}/api/menu-items?enterpriseId=${enterpriseId}`
      : `${API_GATEWAY_CONFIG.baseUrl}/api/menu-items`,
  menuItem: (id: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/menu-items/${id}`,
  generateImage: () => `${API_GATEWAY_CONFIG.baseUrl}/api/menu-items/generate-image`,
  
  // Stores API
  stores: () => `${API_GATEWAY_CONFIG.baseUrl}/api/stores`,
  store: (id: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/stores/${id}`,
  enterpriseStores: (enterpriseId: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/stores?enterpriseId=${enterpriseId}`,
  
  // Employees API
  employees: (enterpriseId?: string) => {
    const baseUrl = `${API_GATEWAY_CONFIG.baseUrl}/api/employees`;
    return enterpriseId ? `${baseUrl}?enterpriseId=${enterpriseId}` : baseUrl;
  },
  employee: (id: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/employees/${id}`,
  jobs: (enterpriseId?: string) => {
    const baseUrl = `${API_GATEWAY_CONFIG.baseUrl}/api/jobs`;
    return enterpriseId ? `${baseUrl}?enterpriseId=${enterpriseId}` : baseUrl;
  },
  
  // Scheduling API
  schedules: (params?: { employeeId?: string; storeId?: string }) => {
    const baseUrl = `${API_GATEWAY_CONFIG.baseUrl}/api/schedules`;
    if (!params) return baseUrl;
    const queryParams = new URLSearchParams();
    if (params.employeeId) queryParams.append('employeeId', params.employeeId);
    if (params.storeId) queryParams.append('storeId', params.storeId);
    const queryString = queryParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  },
  schedule: (id: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/schedules/${id}`,
  
  // Tax Management API
  taxConfig: (entityId?: string, configType?: string) => 
    entityId && configType
      ? `${API_GATEWAY_CONFIG.baseUrl}/api/tax/config/${entityId}/${configType}`
      : `${API_GATEWAY_CONFIG.baseUrl}/api/tax/config`,
  taxCalculate: () => `${API_GATEWAY_CONFIG.baseUrl}/api/tax/calculate`,
  
  // Dayparts API
  dayparts: (enterpriseId: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/dayparts/${enterpriseId}`,
  daypartTemplates: () => `${API_GATEWAY_CONFIG.baseUrl}/api/dayparts/templates`,
  
  // Social Media API
  socialMediaAccounts: () => `${API_GATEWAY_CONFIG.baseUrl}/api/social-media/accounts`,
  socialMediaAccount: (id: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/social-media/accounts/${id}`,
  socialMediaPosts: () => `${API_GATEWAY_CONFIG.baseUrl}/api/social-media/posts`,
  
  // Discounts API
  discounts: (enterpriseId: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/discounts?enterpriseId=${enterpriseId}`,
  discount: (id: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/discounts/${id}`,
  
  // Security Audit API
  securityAudits: (storeId: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/security-audit/stores/${storeId}/audits`,
  securityAudit: (id: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/security-audit/audits/${id}`,
  
  // Categories API
  categories: (enterpriseId: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/enterprises/${enterpriseId}/categories`,
  category: (id: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/categories/${id}`,
  
  // Menu Boards API
  menuBoards: () => `${API_GATEWAY_CONFIG.baseUrl}/api/menu-boards`,
  menuBoard: (id: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/menu-boards/${id}`,
  
  // Network Management API
  networkSites: () => `${API_GATEWAY_CONFIG.baseUrl}/api/network/sites`,
  networkSite: (restaurantId: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/network/sites/${restaurantId}`,
  
  // Messaging API
  messaging: () => `${API_GATEWAY_CONFIG.baseUrl}/api/messaging`,
  messageHistory: (storeId: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/messaging/history/${storeId}`,
  
  // Contact Form API
  contactForm: () => `${API_GATEWAY_CONFIG.baseUrl}/api/contact`,
  
  // POS Sync API
  posSync: () => `${API_GATEWAY_CONFIG.baseUrl}/api/pos/sync`,
  posStoreDiscounts: (storeId: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/pos/sync/discounts/${storeId}`,
  
  // Table Charts API
  tableLibrary: () => `${API_GATEWAY_CONFIG.baseUrl}/api/table-library`,
  restaurantTables: (restaurantId: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/tables/${restaurantId}`,
  
  // Weather API
  weatherCollect: () => `${API_GATEWAY_CONFIG.baseUrl}/api/weather/collect`,
  weatherByStore: (storeId: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/weather/store/${storeId}`,
  
  // Waitlist API
  waitlistEntries: (storeId: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/waitlist/${storeId}`,
  waitlistAdd: (storeId: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/waitlist/${storeId}/add`,
  
  // Payment API
  paymentLinks: () => `${API_GATEWAY_CONFIG.baseUrl}/api/payment-links`,
  paymentLink: (id: string) => `${API_GATEWAY_CONFIG.baseUrl}/api/payment-links/${id}`,
  
  // AI Chat API
  aiChat: () => `${API_GATEWAY_CONFIG.baseUrl}/api/chat`,
  
  // Health Check (no auth required)
  health: () => `${API_GATEWAY_CONFIG.baseUrl}/health`
};

// Helper function to make authenticated API calls
export const apiGatewayFetch = async (url: string, options: RequestInit = {}) => {
  const config = await API_GATEWAY_CONFIG.requestInterceptor({
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  });
  try {
    const response = await fetch(url, config);
    return API_GATEWAY_CONFIG.responseInterceptor.onSuccess(response);
  } catch (error) {
    return API_GATEWAY_CONFIG.responseInterceptor.onError(error);
  }
};

export const migrationHelper = {
  logDeprecatedCall: (oldUrl: string, newUrl: string) => {
    // eslint-disable-next-line no-console
    console.warn(`Deprecated Lambda URL: ${oldUrl} → ${newUrl}`);
  }
};